import { useMemo } from "react";
import { useParams } from "react-router";
import { useGetExplorationQueryParams } from "hooks/useGetExplorationQueryParams";
import {
  type GetClusterIssuesApiV1DatasetDatasetIdClusterClusterIdIssuesGetQueryParams,
  type GetIssuesApiV1DatasetDatasetIdIssueTypesOfEntitiesGetQueryParams,
  useGetClusterIssuesApiV1DatasetDatasetIdClusterClusterIdIssuesGet,
  useGetIssuesApiV1DatasetDatasetIdIssueTypesOfEntitiesGet,
} from "vl-api/vlComponents";

export interface IssueItem {
  id: string;
  text: string;
}

type IssueTypesRow = {
  id: string;
  name: string;
};

function toIssueItems(rows: IssueTypesRow[] | null | undefined): IssueItem[] {
  if (!rows?.length) {
    return [];
  }
  return rows.map((issue) => ({
    id: issue.id,
    text: issue.name,
  }));
}

export const useIssuesData = () => {
  const { datasetId, clusterId } = useParams();
  const explorationQueryParams = useGetExplorationQueryParams();

  const datasetIssuesQueryParams =
    explorationQueryParams as GetIssuesApiV1DatasetDatasetIdIssueTypesOfEntitiesGetQueryParams;
  const clusterIssuesQueryParams =
    explorationQueryParams as GetClusterIssuesApiV1DatasetDatasetIdClusterClusterIdIssuesGetQueryParams;

  const clusterIssuesQuery =
    useGetClusterIssuesApiV1DatasetDatasetIdClusterClusterIdIssuesGet(
      {
        pathParams: { datasetId: datasetId!, clusterId: clusterId! },
        queryParams: clusterIssuesQueryParams,
      },
      {
        refetchOnMount: false,
        enabled: !!clusterId,
      },
    );

  const datasetIssuesQuery =
    useGetIssuesApiV1DatasetDatasetIdIssueTypesOfEntitiesGet(
      {
        pathParams: { datasetId: datasetId! },
        queryParams: datasetIssuesQueryParams,
      },
      {
        refetchOnMount: false,
        enabled: !clusterId,
      },
    );

  const data = clusterId ? clusterIssuesQuery.data : datasetIssuesQuery.data;
  const isLoading = clusterId
    ? clusterIssuesQuery.isLoading
    : datasetIssuesQuery.isLoading;

  const issues = useMemo((): IssueItem[] => {
    return toIssueItems(data as IssueTypesRow[] | null | undefined);
  }, [data]);

  return {
    isLoading,
    issues,
  };
};
